import * as React from 'react';
import { useEffect } from 'react';
import { useRouteData } from 'react-static';
import { white } from '../../common/colors';
import { pushToDataLayer } from '../../common/helpers/dataLayer';
import { useDebug } from '../../common/hooks/useDebug';
import { StyledPaddingContainer } from '../../common/padding.styles';
import { useTranslations } from '../../common/translation';
import { HeadTitle } from '../../components/head-title/HeadTitle';
import { ImageCards } from '../../components/image-cards/ImageCards';
import { ImageCardProps } from '../../components/image-cards/ImageCards.props';
import { RowHeader } from '../../components/row-header/RowHeader';
import { MARKET } from '../../config/browser';
import { DefaultLayout } from '../default/DefaultLayout';
import { LocaleProps } from '../default/DefaultLayout.props';
import { HarryPotterNYBanner } from './promo-banner/HarryPotterNYBanner';
import { Platform934Banner } from './promo-banner/Platform934Banner';
import { StudioTours } from './studio-tours/StudioTours';
import translations from './translations';

export const ABU_DHABI_PARK_URL: string = 'https://www.wbworldabudhabi.com';
export const MOVIE_WORLD_PARK_URL: string = 'https://movieworld.com.au';
export const PARQUE_WARNER_URL: string = 'https://www.parquewarner.com/en';

// TODO: remove after remaining markets have been translated
const harryPotterNYMarkets: string[] = ['CA', 'DE', 'LATAM', 'NL', 'UK', 'US', 'BR'];

const getShopBanners = (): React.ReactNode => {
  const banners = [<Platform934Banner key='platform934' />];

  if (harryPotterNYMarkets.includes(MARKET)) {
    banners.push(<HarryPotterNYBanner key='harry-potter-ny' />);
  }

  if (MARKET === 'US') {
    banners.reverse();
  }

  return banners;
};

// noinspection JSUnusedGlobalSymbols
export default () => {
  const { locale, translatedPaths } = useRouteData<LocaleProps>();
  const { t } = useTranslations(translations, locale);
  useDebug('app:layout:ExperiencesLayout', { props: { locale } });
  useEffect(() => {
    pushToDataLayer({ title: 'experiences' });
  }, []);

  const cards: ImageCardProps[] = [
    {
      alt: 'Warner Bros. World Abu Dhabi',
      src: '/assets/experiences/parks/warner-bros-world-abu-dhabi.jpg',
      subtitle: t('abuDhabiText', 'Abu Dhabi, United Arab Emirates'),
      title: 'Warner Bros. World Abu Dhabi',
      to: ABU_DHABI_PARK_URL,
    },
    {
      alt: 'Warner Bros. Movie World',
      src: '/assets/experiences/parks/warner-bros-movie-world.jpg',
      subtitle: t('goldCoastText', 'Gold Coast, Australia'),
      title: 'Warner Bros. Movie World',
      to: MOVIE_WORLD_PARK_URL,
    },
    {
      alt: 'Parque Warner',
      src: '/assets/experiences/parks/parque-warner.jpg',
      subtitle: t('madridText', 'Madrid, Spain'),
      title: 'Parque Warner',
      to: PARQUE_WARNER_URL,
    },
  ];

  return (
    <DefaultLayout bgColor={white} locale={locale} hideBottomPadding={true} translatedPaths={translatedPaths}>
      <HeadTitle title={t('pageTitle', 'Experiences')} />
      <StudioTours />
      <section>
        <RowHeader headingTag='h1' title={t('shopTitle', 'Shop')} />
        {getShopBanners()}
      </section>
      <section>
        <RowHeader headingTag='h1' title={t('parksTitle', 'Parks')} />
        <StyledPaddingContainer data-testid='parks-image-cards'>
          <ImageCards cards={cards} headingTag='h2' lazyLoad={true} ratio={0.8} />
        </StyledPaddingContainer>
      </section>
    </DefaultLayout>
  );
};
